import { hasClass, loadScript, select, selectAll, on, addClass, removeClass } from 'helpers/dom'
import debounce from 'lodash.debounce'

/**
 * Selectors and constants used throughout the video player component
 */
const selectors = {
  VIDEO_PLAYER_SELECTOR: 'video-player',
  VIDEO_THUMBNAIL_SELECTOR: '.js-video-player-thumbnail',
  VIDEO_ONLY_BUTTON: 'js-video-only-button',
  VIDEO_THUMBNAIL_BUTTON_SELECTOR: '.js-video-player-thumbnail-button',
  VIDEO_PAUSE_BUTTON_SELECTOR: '.js-pause-video',
  VIDEO_SOUND_SELECTOR: '.js-sound-video',
  HIDDEN_CLASS: 'opacity-0',
  ACTION_PAUSING_CLASS: 'is-action-pausing',
  PLAYING_CLASS: 'is-playing',
  LOADING_CLASS: 'is-loading',
  PAUSING_CLASS: 'is-pausing',
  MUTED_CLASS: 'is-muted',
  VIDEO_TYPES: {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo',
    MP4: 'mp4'
  },
  STATES: {
    PLAYING: 'playing',
    PAUSING: 'pausing',
    LOADING: 'loading'
  },
  DEBOUNCE_TIME: 300
}

/**
 * Custom element that handles video playback from YouTube, Vimeo or MP4 sources
 */
export default class VideoPlayer extends HTMLElement {
  /**
   * Initialize video player and set up configuration
   */
  constructor() {
    super()
    this.player = null
    this.element = document.createElement('div')
    this.type = this.getAttribute('data-video-type') || selectors.VIDEO_TYPES.MP4
    this.thumbnailEl = null
    this.buttonEl = null
    this.isLoaded = false
    this.src = this.getAttribute('src') || null
    this.status = {
      play: null,
      error: null
    }
    this.wasPlayingBeforeHidden = false

    // Generate a unique ID for this video player instance if it doesn't have one
    this.videoId = this.getAttribute('id') || `video-${Math.random().toString(36).substring(2, 9)}`

    if (!this.getAttribute('id')) {
      this.setAttribute('id', this.videoId)
    }

    // Check if this video has ever played in this session
    try {
      this.hasEverPlayed = sessionStorage.getItem(`video-played-${this.videoId}`) === 'true'
    } catch (e) {
      this.hasEverPlayed = false
    }
    this.options = {
      autoplay: this.hasAttribute('autoplay'),
      controls: this.hasAttribute('controls'),
      playsinline: this.hasAttribute('playsinline'),
      muted: this.hasAttribute('muted'),
      loop: this.hasAttribute('loop'),
      origin: window.location.origin,
      thumbnail: this.getAttribute('data-thumbnail'),
      button: this.getAttribute('data-video-button'),
      playIntersection: this.hasAttribute('data-play-intersection'),
      lazy: this.hasAttribute('lazy'),
      disableButton: this.hasAttribute('disable-button'),
      controlSound: this.hasAttribute('control-sound'),
      hideThumbnail: this.hasAttribute('hide-thumbnail')
    }
    this.isVideoMuted = this.hasAttribute('muted')
    this.playOnClick = false
    this.videos = selectAll(selectors.VIDEO_PLAYER_SELECTOR)

    if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
      this.appendChild(this.element)
    }

    if (!this.src) {
      this.status.error = 'No video src'
    }

    if (this.options.playIntersection) {
      this.initWhenVisible()
    }
    this.unload = this.unload.bind(this)
    this.play = this.play.bind(this)
    this.pause = this.pause.bind(this)
    this.initWhenVisible = this.initWhenVisible.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)

    // Add visibility change listener to handle tab switching
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    if (this.hasAttribute('auto-init')) {
      this.init()
    }

    if (this.hasAttribute('lazy')) {
      this.lazy()
    }
  }

  /**
   * Extract video ID from YouTube or Vimeo URLs
   */
  get getVideoID() {
    if (this.getAttribute('data-external-id')) return this.getAttribute('data-external-id')

    if (/youtu\.?be/.test(this.src)) {
      const patterns = [
        /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
        /\?v=([^#&?]{11})/, // ?v=<id>
        /&v=([^#&?]{11})/, // &v=<id>
        /embed\/([^#&?]{11})/, // embed/<id>
        /\/v\/([^#&?]{11})/ // /v/<id>
      ]

      for (let i = 0; i < patterns.length; ++i) {
        if (patterns[i].test(this.src)) {
          return patterns[i].exec(this.src)[1]
        }
      }
    }

    if (/^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.test(this.src)) {
      const parseURL = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.exec(this.src)

      return parseURL[5]
    }

    return null
  }

  /**
   * Initialize player when it becomes visible in viewport
   */
  initWhenVisible() {
    this.observeVisibility(this).then((isVisible) => {
      if (!this.isLoaded && isVisible) {
        if (this.options.autoplay) {
          this.initThumbnail()

          if (this.options.button) {
            this.initButton()
          }
        }
        this.src && this.type && this.init()
        this.isLoaded = true
      }
    })
  }

  /**
   * Unload current video player
   */
  unload() {
    switch (this.type) {
      case 'youtube':
        this.player.clearVideo()
        break
      case 'vimeo':
        this.player.unload()
        break
    }
  }

  /**
   * Initialize sound control UI and handlers
   */
  initControlSound() {
    const iconMute = `<svg xmlns="http://www.w3.org/2000/svg" id="icon-mute" class="w-[30px] h-[30px] text-white" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M7.5 15.75H3a.75.75 0 0 1-.75-.75V9A.75.75 0 0 1 3 8.25h4.5L14.25 3v18L7.5 15.75ZM22.5 9.75l-4.5 4.5M22.5 14.25 18 9.75M7.5 8.25v7.5"/>
    </svg>`
    const iconUnMute = `<svg xmlns="http://www.w3.org/2000/svg" id="icon-unmute" class="w-[30px] h-[30px] text-white" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M20.522 7.228a6.743 6.743 0 0 1 0 9.544M7.5 15.75H3a.75.75 0 0 1-.75-.75V9A.75.75 0 0 1 3 8.25h4.5L14.25 3v18L7.5 15.75ZM7.5 8.25v7.5M17.869 9.881a2.99 2.99 0 0 1 0 4.238"/>
    </svg>`
    const buttonSoundVideo = `
      <button type="button" class="absolute cursor-pointer top-2.5 right-2.5 z-[9] w-[30px] h-[30px] js-sound-video" aria-label="Toggle mute">
        <span class="absolute top-0 left-0 hidden [.is-muted_&]:!block" aria-hidden="true">${iconMute}</span>
        <span class="absolute top-0 left-0 block [.is-muted_&]:!hidden" aria-hidden="true">${iconUnMute}</span>
      </button>
    `

    this.insertAdjacentHTML('beforeend', buttonSoundVideo)

    const soundVideoEl = select(selectors.VIDEO_SOUND_SELECTOR, this)

    if (soundVideoEl) {
      on(
        'click',
        (e) => {
          if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
            if (this.isVideoMuted) {
              this.player.unMute()
            } else {
              this.player.mute()
            }
          }

          if (this.type === selectors.VIDEO_TYPES.VIMEO) {
            if (this.isVideoMuted) {
              this.player.setVolume(1)
            } else {
              this.player.setVolume(0)
            }
          }

          if (this.type === selectors.VIDEO_TYPES.MP4) {
            this.player.muted = !this.isVideoMuted
          }

          this.isVideoMuted = !this.isVideoMuted

          if (this.isVideoMuted) {
            addClass(selectors.MUTED_CLASS, this)
          } else {
            removeClass(selectors.MUTED_CLASS, this)
          }
        },
        soundVideoEl
      )
    }
  }

  /**
   * Initialize play/pause button UI and click handlers
   */
  initButton() {
    const html = `
      <div class="js-video-player-thumbnail js-video-only-button absolute left-0 top-0 w-full h-full flex items-center justify-center cursor-pointer">
          <button type="button" class="js-video-player-thumbnail-button rounded-full text-while absolute h-12 w-12 flex z-50 items-center justify-center" aria-label="Play/pause Video">
            <div class="[.is-loading_&]:hidden">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="46" height="46" rx="23" stroke="white" stroke-width="2"/>
                <path d="M32.3844 23.3625L18.8937 15.1125C18.7802 15.0425 18.65 15.004 18.5166 15.001C18.3832 14.9981 18.2514 15.0308 18.1349 15.0957C18.0183 15.1607 17.9212 15.2556 17.8535 15.3706C17.7859 15.4856 17.7501 15.6166 17.75 15.75V32.25C17.7501 32.3835 17.7859 32.5144 17.8535 32.6295C17.9212 32.7445 18.0183 32.8393 18.1349 32.9043C18.2514 32.9693 18.3832 33.002 18.5166 32.999C18.65 32.9961 18.7802 32.9576 18.8937 32.8875L32.3844 24.6375C32.4952 24.572 32.5869 24.4788 32.6507 24.367C32.7145 24.2552 32.748 24.1287 32.748 24C32.748 23.8713 32.7145 23.7449 32.6507 23.6331C32.5869 23.5213 32.4952 23.428 32.3844 23.3625Z" fill="white"/>
              </svg>
            </div>
            <div class="hidden [.is-loading_&]:block">
              <span class="button-loading after:w-8 after:h-8"></span>
            </div>
          </button>
        </div>
      </div>
      `

    this.buttonEl = html
    this.insertAdjacentHTML('beforeend', this.buttonEl)

    const btnEl = this.querySelector(selectors.VIDEO_THUMBNAIL_SELECTOR)

    if (this.options.autoplay) {
      this.setStatePlayer(selectors.STATES.LOADING)
    }
    btnEl.addEventListener('click', () => {
      if (!this.player) {
        this.setStatePlayer(selectors.STATES.LOADING)

        if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
          this.setupYTPlayer()
        }

        if (this.type === selectors.VIDEO_TYPES.VIMEO) {
          this.setupVimeoPlayer()
        }

        if (this.type === selectors.VIDEO_TYPES.MP4) {
          this.setupMP4Player()
        }
      } else {
        if (!this.status.play) {
          this.play()
          this.classList.remove(selectors.ACTION_PAUSING_CLASS)
        } else {
          this.pause()
          this.classList.add(selectors.ACTION_PAUSING_CLASS)
        }
      }
    })
  }

  /**
   * Initialize video thumbnail/preview UI
   */
  initThumbnail() {
    // Skip creating thumbnail overlay if the parent already has a background image style
    const hasInlineStyle = this.hasAttribute('style') && this.getAttribute('style').includes('background-image')

    const styleThumbnail = this.options.thumbnail && !hasInlineStyle
      ? `background-image: url(${this.options.thumbnail}); background-size: cover; background-position: center;`
      : 'background-color: transparent'
    const initialClass = this.options.hideThumbnail
      ? `js-video-player-thumbnail z-10 video-player-thumbnail absolute left-0 top-0 w-full h-full bg-grey-500 flex items-center justify-center smooth-render ${selectors.HIDDEN_CLASS}`
      : 'js-video-player-thumbnail z-10 video-player-thumbnail absolute left-0 top-0 w-full h-full bg-grey-500 flex items-center justify-center smooth-render'
    const html = `
        <div class="${initialClass}" style="${styleThumbnail}">
          <button type="button" class="js-video-player-thumbnail-button ${
            this.options.disableButton && this.options.autoplay ? 'hidden' : ''
          } rounded-full text-while absolute h-12 w-12 flex z-10 items-center justify-center smooth-render" aria-label="Play video">
            <div class="[.is-loading_&]:hidden" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="46" height="46" rx="23" stroke="#fff" stroke-width="2"/>
                <path d="m32.384 23.363-13.49-8.25a.75.75 0 0 0-1.144.637v16.5a.75.75 0 0 0 1.144.638l13.49-8.25a.741.741 0 0 0 0-1.276Z" fill="#fff"/>
              </svg>
            </div>
            <div class="hidden [.is-loading_&]:block" aria-hidden="true">
              <span class="button-loading button-loading-large"></span>
            </div>
          </button>
        </div>
      `
    const htmlButtonPauseVideo =
      '<div class="js-pause-video absolute top-0 left-0 w-full h-full bg-transparent z-[5]"></div>'

    this.thumbnailEl = html
    this.insertAdjacentHTML('beforeend', this.thumbnailEl)

    if (!this.options.controls && !this.options.autoplay) {
      this.insertAdjacentHTML('beforeend', htmlButtonPauseVideo)

      const buttonPauseVideoEl = this.querySelector(selectors.VIDEO_PAUSE_BUTTON_SELECTOR)

      buttonPauseVideoEl &&
        buttonPauseVideoEl.addEventListener('click', () => {
          if (this.status.play) {
            this.onPause()
          } else {
            this.onPlay()
          }
        })
    }

    const buttonEl = this.querySelector(selectors.VIDEO_THUMBNAIL_BUTTON_SELECTOR)

    if (this.options.autoplay) {
      this.setStatePlayer(selectors.STATES.LOADING)
    }

    buttonEl &&
      buttonEl.addEventListener('click', () => {
        // Dispatch custom event to notify parent components about thumbnail click
        this.dispatchEvent(new CustomEvent('video-thumbnail-click', {
          bubbles: true,
          detail: { willPlay: !this.player || !this.status.play }
        }))

        this.pauseAllSingleVideos()

        if (!this.player) {
          this.setStatePlayer(selectors.STATES.LOADING)
          this.playOnClick = true

          if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
            this.setupYTPlayer()
          }

          if (this.type === selectors.VIDEO_TYPES.VIMEO) {
            this.setupVimeoPlayer()
          }

          if (this.type === selectors.VIDEO_TYPES.MP4) {
            this.setupMP4Player()
          }
        } else {
          if (!this.status.play) {
            this.play()
          } else {
            this.pause()
          }
        }
      })
  }

  /**
   * Initialize YouTube player specific functionality
   */
  initYoutubePlayer() {
    this.initThumbnail()

    if (this.options.button) {
      this.initButton()
    }

    if (this.options.autoplay) {
      YT.ready(() => this.setupYTPlayer())
    }
  }

  /**
   * Initialize Vimeo player specific functionality
   */
  initVimeoPlayer() {
    this.initThumbnail()

    if (this.options.button) {
      this.initButton()
    }

    if (this.options.autoplay) {
      this.setupVimeoPlayer()
    }
  }

  /**
   * Initialize MP4 player specific functionality
   */
  initMP4Player() {
    if (this.querySelector('video')) return

    this.initThumbnail()

    if (this.options.button) {
      this.initButton()
    }

    if (this.options.autoplay) {
      this.setupMP4Player()
    }
  }

  /**
   * Initialize player based on video type
   */
  async initByType(type) {
    switch (type) {
      case selectors.VIDEO_TYPES.YOUTUBE:
        this.initYoutubePlayer()
        break
      case selectors.VIDEO_TYPES.VIMEO:
        this.initVimeoPlayer()
        break
      case selectors.VIDEO_TYPES.MP4:
        this.initMP4Player()
        break
    }
  }

  /**
   * Load required player APIs based on video type
   */
  async loadPlayerAPIs(type) {
    let src = ''

    if (type === selectors.VIDEO_TYPES.YOUTUBE && !window.YT) {
      src = 'https://www.youtube.com/iframe_api'
    } else {
      if (type === selectors.VIDEO_TYPES.VIMEO && !window.Vimeo) {
        src = 'https://player.vimeo.com/api/player.js'
      } else {
        return
      }
    }

    return loadScript(src)
  }

  /**
   * Set up YouTube player instance
   */
  setupYTPlayer() {
    const options = {
      videoId: this.getVideoID,
      playerVars: {
        autoplay: this.options.autoplay ? 1 : 0,
        controls: this.options.controls ? 1 : 0,
        height: '100%',
        width: '100%',
        playsinline: this.options.playsinline ? 1 : 0,
        loop: this.options.loop ? 1 : 0
      },
      events: {
        onError: () => {
          this.status.error = new Error('player could not be built')
        },
        onStateChange: (e) => this._onYouTubeStateChange(e),
        onReady: (e) => this._onYoutubeReadyState(e)
      }
    }

    if (YT.loaded) {
      this.player = new window.YT.Player(this.element, options)
    }
  }

  /**
   * Set up Vimeo player instance
   */
  setupVimeoPlayer() {
    if (this.player) return

    const options = {
      id: this.getVideoID,
      background: !this.options.controls,
      autopause: false,
      ...this.options
    }

    this.player = new window.Vimeo.Player(this, options)

    this.player.ready().then(() => {
      // Hide thumbnail immediately if hide-thumbnail option is set
      if (this.options.hideThumbnail) {
        this.displayThumbnail(false)
      }
      this.play()
    })

    this.player.on('play', () => {
      setTimeout(() => {
        this.onPlay()
      }, 300)
    })
    this.player.on('pause', () => this.onPause())
    this.player.on('ended', () => this.onEnd())
  }

  /**
   * Set up HTML5 video player instance
   */
  setupMP4Player() {
    this.videoEl = document.createElement('video')
    this.videoEl.src = this.src
    this.player = this.videoEl
    this.appendChild(this.videoEl)

    if (!this.options.autoplay) {
      this.onPlay()
      this.setStatePlayer(selectors.STATES.PLAYING)
    }

    // If hide-thumbnail option is set, hide it regardless of player state
    if (this.options.hideThumbnail) {
      this.displayThumbnail(false)
    } else {
      this.displayThumbnail(false)
    }

    this.player.controls = this.options.controls
    this.player.autoplay = this.options.autoplay
    this.player.playsInline = this.options.playsinline
    this.player.muted = this.options.muted
    this.player.loop = this.options.loop

    this.player.addEventListener('play', () => {
      if (
        (!this.videoEl.paused && this.videoEl.currentTime > 0 && !this.videoEl.ended && this.options.controls) ||
        !this.options.controls
      ) {
        this.onPlay()
      }
    })
    this.player.addEventListener('pause', () => this.onPause())
    this.player.addEventListener('ended', () => this.onEnd())
  }

  /**
   * Start video playback
   */
  play() {
    try {
      if (!this.player) return

      this.pauseAllSingleVideos()
      if (this.type === selectors.VIDEO_TYPES.VIMEO && !this.options.muted) this.player.setVolume(1)

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.playVideo()
      } else {
        this.player.play()
      }
      on(
        'play',
        () => {
          this.displayThumbnail(false)
        },
        this
      )
    } catch (error) {
      this.status.error = error
    }
  }

  /**
   * Pause video playback
   */
  pause() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.pauseVideo()
      } else {
        this.player.pause()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  /**
   * Handle play event
   */
  onPlay() {
    this.play()
    this.status.play = true
    this.hasEverPlayed = true

    // Store that this video has played in session storage
    try {
      sessionStorage.setItem(`video-played-${this.videoId}`, 'true')
    } catch (e) {
      // Ignore storage errors
    }

    // If this player has a direct background style, remove it when video plays
    if (this.hasAttribute('style') && this.getAttribute('style').includes('background-image')) {
      // Fade out the background image
      this.style.transition = 'background-image 0.3s ease-out'
      this.style.backgroundImage = 'none'
    }

    this.dispatchEvent(new CustomEvent('play'))
    on(
      'play',
      () => {
        this.setStatePlayer(selectors.STATES.PLAYING)
      },
      this
    )
  }

  /**
   * Handle pause event
   */
  onPause() {
    this.pause()
    this.status.play = false
    this.dispatchEvent(new CustomEvent('pause'))
    this.setStatePlayer(selectors.STATES.PAUSING)

    // Only show thumbnail on pause if video has never been played before
    // This ensures the thumbnail stays hidden after first play
    if (!this.hasEverPlayed) {
      this.displayThumbnail(true)
    }
  }

  /**
   * Handle end event
   */
  onEnd() {
    if (this.options.loop) {
      this.play()
    } else {
      this.status.play = false
      this.dispatchEvent(new CustomEvent('ended'))

      // Only show thumbnail when video ends if it has never played before
      if (!this.hasEverPlayed) {
        this.displayThumbnail(true)
      }

      this.setStatePlayer(null)
    }
  }

  /**
   * Set player state CSS classes
   */
  setStatePlayer(status) {
    switch (status) {
      case selectors.STATES.PLAYING:
        this.classList.add(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.PAUSING:
        this.classList.add(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.LOADING:
        this.classList.add(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        break
      default:
        this.classList.remove(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
    }
  }

  /**
   * Set iframe height for proper video display
   */
  setIframeHeight() {
    const iframe = this.querySelector('iframe')

    if (iframe) {
      iframe.style.height = '100%'
      iframe.style.width = '100%'
    }
  }

  /**
   * Show/hide video thumbnail
   */
  displayThumbnail(status) {
    const el = this.querySelector(selectors.VIDEO_THUMBNAIL_SELECTOR)

    if (!el) return

    // If video has ever played, always keep thumbnail hidden
    if (this.hasEverPlayed) {
      el.classList.add(selectors.HIDDEN_CLASS)
      this.setIframeHeight()

      if (!el.classList.contains(selectors.VIDEO_ONLY_BUTTON)) {
        el.classList.remove('visible')
        el.classList.add('invisible')
      }

      return
    }

    if (status) {
      el.classList.remove(selectors.HIDDEN_CLASS)
      this.setIframeHeight()

      if (!el.classList.contains(selectors.VIDEO_ONLY_BUTTON)) {
        el.classList.add('visible')
        el.classList.remove('invisible')
      }
    } else {
      el.classList.add(selectors.HIDDEN_CLASS)
      this.setIframeHeight()

      if (!el.classList.contains(selectors.VIDEO_ONLY_BUTTON)) {
        el.classList.remove('visible')
        el.classList.add('invisible')
      }
    }
  }

  /**
   * Handle YouTube player state changes
   */
  _onYouTubeStateChange(e) {
    const stateMap = {
      '-1': 'unstarted',
      0: 'ended',
      1: 'playing',
      2: 'pause',
      3: 'buffering',
      5: 'cued'
    }
    const state = stateMap[e.data.toString()]
    const eventMethodMap = {
      ended: this.onEnd,
      pause: this.onPause,
      playing: this.onPlay
    }

    if (eventMethodMap[state]) {
      const method = eventMethodMap[state]

      if (method) {
        method.call(this)
      }
    }
  }

  /**
   * Handle YouTube player ready state
   */
  _onYoutubeReadyState(e) {
    if (this.options.autoplay || this.playOnClick) {
      this.onPlay()
    }

    if (this.options.muted && this.player?.mute) {
      this.player.mute()
    }

    // Hide thumbnail immediately if hide-thumbnail option is set
    if (this.options.hideThumbnail) {
      this.displayThumbnail(false)
    }
  }

  /**
   * Disconnected callback for custom element lifecycle
   * Clean up event listeners when element is removed from DOM
   */
  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  /**
   * Watch for element visibility changes
   */
  observeVisibility(element) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting

          resolve(isVisible)
        })
      })

      observer.observe(element)
    })
  }

  /**
   * Pause other video players with single-play attribute
   */
  pauseAllSingleVideos() {
    if (this.videos.length) {
      this.videos.forEach((video) => {
        if (video !== this && video.hasAttribute('single-play') && video.status.play) {
          video.onPause()
        }
      })
    }
  }

  /**
   * Initialize lazy loading
   */
  lazy() {
    const observer = new IntersectionObserver(
      debounce((entries) => {
        entries.forEach((entry) => {
          if (this.options.lazy) {
            if (entry.isIntersecting) {
              if (!this.isLoaded) {
                this.initWhenVisible()
              } else {
                if (this.options.autoplay) {
                  this.player && !this.hasAttribute('single-play') && !this.status.play && this.onPlay()
                }
              }
            } else {
              this.player && !this.hasAttribute('single-play') && this.status.play && this.onPause()
            }
          }
        })
      }, selectors.DEBOUNCE_TIME)
    )

    observer.observe(this)
  }

  /**
   * Handle document visibility changes (switching tabs)
   * Pauses video when tab is hidden, resumes when visible if it was playing before
   */
  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      // Tab is now hidden, store current play state and pause
      this.wasPlayingBeforeHidden = this.status.play

      if (this.status.play) {
        this.pause()
      }
    } else {
      if (document.visibilityState === 'visible') {
        // Tab is now visible, resume if it was playing before
        if (this.wasPlayingBeforeHidden && this.player && this.options.autoplay) {
          // If video has ever played, make sure thumbnail stays hidden
          if (this.hasEverPlayed) {
            this.displayThumbnail(false)
          }
          this.play()
        }
      }
    }
  }

  /**
   * Initialize video player
   */
  async init() {
    this.type !== selectors.VIDEO_TYPES.MP4 && (await this.loadPlayerAPIs(this.type))
    this.initByType(this.type)

    if (this.options.controlSound && !this.options.controls) {
      this.initControlSound()
    }
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer)
}
